import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import logo from '../assets/Header-blue.svg';
import { useAuth } from '../context/AuthContext';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { login, user, loading } = useAuth();
  const navigate = useNavigate();

  const slides = [
    {
      title: 'Master Task & Case Management',
      description:
        'Efficiently organize your mediations, assign tasks, set deadlines, and track progress.',
    },
    {
      title: 'Streamline Your Workflow',
      description:
        'Manage cases, inquiries, billing, and scheduling all from one unified platform.',
    },
    {
      title: 'Data-Driven Insights',
      description:
        'Track mediator performance, case trends, and financial metrics with real-time analytics.',
    },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!loading && user) navigate('/');
  }, [user, loading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid email or password');
      setSubmitting(false);
    }
  };

  return (
    <div className="h-screen max-h-screen w-full flex flex-col md:flex-row bg-white overflow-hidden">
      {/* Left Panel - Form Area */}
      <div className="w-full md:w-1/2 h-screen max-h-screen flex flex-col p-6 md:p-8 lg:p-10 bg-white overflow-hidden shrink-0">
        {/* Logo */}
        <div className="mb-4 shrink-0">
          <img src={logo} alt="C.A.R. Dispute Resolution Center" className="h-12 object-contain" />
        </div>

        {/* Centered Form */}
        <div className="flex-grow min-h-0 flex flex-col justify-center max-w-md w-full mx-auto py-4">
          <div className="mb-8">
            <h1
              className="text-[30px] font-bold text-[#0e1e20] mb-2"
              style={{ fontFamily: "'Inter Tight', sans-serif" }}
            >
              Case Manager Portal
            </h1>
            <p
              className="text-[#5b5a64] text-base"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Sign in to access the mediation management system.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200">
                <p className="text-sm text-red-600" style={{ fontFamily: "'Inter', sans-serif" }}>{error}</p>
              </div>
            )}
            {/* Email */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-[#0e1e20] mb-1.5"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@scalemediation.com"
                required
                className="w-full px-4 py-3 rounded-lg border border-[#e5e5e5] text-sm text-[#0e1e20] placeholder-[#9ca3af] outline-none transition-all focus:border-[#115e59] focus:ring-2 focus:ring-[#115e59]/20"
                style={{ fontFamily: "'Inter', sans-serif" }}
              />
            </div>

            {/* Password */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-[#0e1e20] mb-1.5"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  className="w-full px-4 py-3 pr-12 rounded-lg border border-[#e5e5e5] text-sm text-[#0e1e20] placeholder-[#9ca3af] outline-none transition-all focus:border-[#115e59] focus:ring-2 focus:ring-[#115e59]/20"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9ca3af] hover:text-[#5b5a64] transition-colors"
                >
                  {showPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                      <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Remember Me + Forgot Password */}
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 rounded border-[#e5e5e5] text-[#115e59] focus:ring-[#115e59]"
                />
                <label
                  htmlFor="remember-me"
                  className="ml-2 block text-sm text-[#5b5a64]"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Remember Me
                </label>
              </div>
              <Link
                to="/forgot-password"
                className="text-sm font-medium text-[#115e59] hover:text-[#0f766e] transition-colors"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Forgot Password?
              </Link>
            </div>

            {/* Sign In Button */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#115e59] text-white py-3 rounded-lg font-medium hover:bg-[#0f766e] active:bg-[#134e4a] transition-colors text-base disabled:opacity-60"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              {submitting ? 'Signing In...' : 'Sign In'}
            </button>
          </form>
        </div>

        {/* Footer */}
        <div
          className="mt-4 text-xs text-[#5b5a64] shrink-0"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          &copy; 2026 Scale Mediation
        </div>
      </div>

      {/* Right Panel - Teal Visual Area */}
      <div className="hidden md:flex w-1/2 h-screen max-h-screen relative overflow-hidden flex-col items-center justify-center p-6 lg:p-8 text-center shrink-0">
        {/* Gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#115e59] via-[#0d5450] to-[#0d4f4a]" />

        {/* Dashboard Preview Card */}
        <div className="relative z-10 w-full max-w-[560px] mb-8">
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-1 ring-1 ring-white/20 shadow-2xl">
            <div className="bg-white rounded-xl p-5 space-y-4">
              {/* Mock Header */}
              <div className="flex items-center justify-between">
                <img src={logo} alt="" className="h-7 object-contain" />
                <div className="flex gap-2">
                  <div className="w-7 h-7 rounded-lg bg-[#f1f1f5]" />
                  <div className="w-7 h-7 rounded-lg bg-[#f1f1f5]" />
                </div>
              </div>

              {/* KPI Cards Row */}
              <div className="grid grid-cols-4 gap-2.5">
                {[
                  { label: 'Active Cases', value: '127', color: '#115e59' },
                  { label: 'Pending', value: '34', color: '#0f766e' },
                  { label: 'Resolved', value: '89', color: '#14b8a6' },
                  { label: 'Revenue', value: '$42K', color: '#5b5a64' },
                ].map((kpi, i) => (
                  <div
                    key={i}
                    className="rounded-lg p-2.5"
                    style={{ backgroundColor: '#f8fffe' }}
                  >
                    <div
                      className="text-[10px] text-[#5b5a64] mb-1 truncate"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      {kpi.label}
                    </div>
                    <div
                      className="text-sm font-bold"
                      style={{ color: kpi.color, fontFamily: "'Inter Tight', sans-serif" }}
                    >
                      {kpi.value}
                    </div>
                  </div>
                ))}
              </div>

              {/* Chart Placeholder */}
              <div className="bg-[#f8fffe] rounded-lg p-3">
                <div
                  className="text-[10px] text-[#5b5a64] mb-2"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Monthly Cases
                </div>
                <div className="flex items-end gap-1.5 h-16">
                  {[40, 65, 50, 80, 60, 90, 75, 85, 55, 70, 95, 68].map((h, i) => (
                    <div
                      key={i}
                      className="flex-1 rounded-sm transition-all"
                      style={{
                        height: `${h}%`,
                        backgroundColor: i === 10 ? '#115e59' : '#a7d8d4',
                      }}
                    />
                  ))}
                </div>
              </div>

              {/* Table Rows */}
              <div className="space-y-1.5">
                {[1, 2, 3].map((row) => (
                  <div key={row} className="flex items-center gap-2 py-1.5 px-2 rounded-md bg-[#f8fffe]">
                    <div className="w-5 h-5 rounded-full bg-[#a7d8d4]" />
                    <div className="flex-1">
                      <div className="h-2 bg-[#e5e5e5] rounded-full w-3/4" />
                    </div>
                    <div className="h-2 bg-[#e5e5e5] rounded-full w-12" />
                    <div
                      className="text-[8px] px-1.5 py-0.5 rounded-full font-medium"
                      style={{
                        backgroundColor: row === 1 ? '#d1fae5' : row === 2 ? '#fef3c7' : '#dbeafe',
                        color: row === 1 ? '#065f46' : row === 2 ? '#92400e' : '#1e40af',
                      }}
                    >
                      {row === 1 ? 'Active' : row === 2 ? 'Pending' : 'Closed'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Text Carousel */}
        <div className="relative z-20 max-w-lg mx-auto">
          <div className="transition-all duration-500 ease-in-out">
            <h2
              className="text-2xl font-bold text-white mb-3 tracking-tight"
              style={{ fontFamily: "'Inter Tight', sans-serif" }}
            >
              {slides[currentSlide].title}
            </h2>
            <p
              className="text-white/80 text-base leading-relaxed"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              {slides[currentSlide].description}
            </p>
          </div>

          {/* Dot Indicators */}
          <div className="flex justify-center gap-3 mt-6">
            {slides.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentSlide(idx)}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  idx === currentSlide
                    ? 'w-12 bg-white'
                    : 'w-12 bg-white/30 hover:bg-white/50'
                }`}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
